import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LEGAL } from "../config/legal";

/**
 * useAnalytics — SPA page_view tracker for GA4.
 *
 * GA4 is loaded LAZILY and only after the visitor has granted analytics
 * consent (localStorage "liap-analytics-consent" === "granted"). The
 * Google Consent Mode default in index.html is 'denied' for every
 * category, so nothing is measured until consent is explicit.
 *
 * The visitor's detailed choices are persisted in localStorage
 * "liap-consent-preferences" ({ version, necessary, analytics, decidedAt })
 * so the preferences panel always reopens showing the visitor's actual
 * previous choices rather than a generic granted/denied state.
 * Withdrawing consent also removes any GA cookies that were set.
 */

const GA_ID = LEGAL.ga4MeasurementId;
const STATE_KEY = "liap-analytics-consent";
const PREFS_KEY = "liap-consent-preferences";
let loaded = false;

function loadGA() {
    if (loaded) return;
    loaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    if (window.gtag) {
        window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
    }
}

function saveConsentPreferences(analytics) {
    try {
        window.localStorage.setItem(PREFS_KEY, JSON.stringify({
            version: 1,
            necessary: true,
            analytics: !!analytics,
            decidedAt: new Date().toISOString(),
        }));
    } catch (_e) { /* noop */ }
}

/**
 * getConsentPreferences — the visitor's actual stored choices.
 * Falls back to the legacy granted/denied key for visits that predate
 * the detailed store, so the panel never resets to a generic state.
 */
export function getConsentPreferences() {
    try {
        const raw = window.localStorage.getItem(PREFS_KEY);
        if (raw) {
            const p = JSON.parse(raw);
            return { necessary: true, analytics: !!p.analytics, decidedAt: p.decidedAt || null };
        }
        const legacy = getConsentState();
        if (legacy === "granted" || legacy === "denied") {
            return { necessary: true, analytics: legacy === "granted", decidedAt: null };
        }
        return null;
    } catch (_e) { return null; }
}

/** Remove GA identifiers from the browser when consent is withdrawn. */
function clearGaCookies() {
    try {
        const names = document.cookie
            .split(";")
            .map((c) => c.trim().split("=")[0])
            .filter((n) => n === "_ga" || n === "_gid" || n === "_gat" || n.startsWith("_ga_") || n.startsWith("_gat_"));
        const host = window.location.hostname;
        names.forEach((name) => {
            [host, `.${host}`].forEach((domain) => {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
            });
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
    } catch (_e) { /* noop */ }
}

export function grantAnalyticsConsent() {
    try { window.localStorage.setItem(STATE_KEY, "granted"); } catch (_e) { /* noop */ }
    saveConsentPreferences(true);
    if (window.gtag) {
        window.gtag("consent", "update", { analytics_storage: "granted" });
    }
    loadGA();
    if (window.gtag) {
        window.gtag("event", "page_view", {
            page_path: window.location.pathname + window.location.search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }
}

export function denyAnalyticsConsent() {
    try { window.localStorage.setItem(STATE_KEY, "denied"); } catch (_e) { /* noop */ }
    saveConsentPreferences(false);
    if (window.gtag) {
        window.gtag("consent", "update", { analytics_storage: "denied" });
    }
    clearGaCookies();
}

export function getConsentState() {
    try { return window.localStorage.getItem(STATE_KEY); }
    catch (_e) { return null; }
}

export default function useAnalytics() {
    const location = useLocation();

    // On first mount: if consent was previously granted, load GA now.
    useEffect(() => {
        if (getConsentState() === "granted") {
            if (window.gtag) {
                window.gtag("consent", "update", { analytics_storage: "granted" });
            }
            loadGA();
        }
    }, []);

    // On every route change: fire page_view only if consent is granted.
    useEffect(() => {
        if (location.pathname.startsWith("/admin")) return; // never measure the private inbox
        if (!window.gtag) return;
        if (getConsentState() !== "granted") return;
        window.gtag("event", "page_view", {
            page_path: location.pathname + location.search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [location.pathname, location.search, location.hash]);
}
